import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuth } from '../stores/auth';
import api from '../lib/api';
import { MapPin, Route, Camera, User, RefreshCw, CheckCircle, Trophy, MessageCircle, GraduationCap, Target, ClipboardList } from 'lucide-react';
import JourneyCard from '../components/journey/JourneyCard';
import ExpenseButton from '../components/journey/ExpenseButton';

export default function Home() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const { data: todayRoute, isLoading } = useQuery({
    queryKey: ['route', 'today'],
    queryFn: () => api.get('/routes/today').then(res => res.data.data),
  });

  const { data: todayVisits } = useQuery({
    queryKey: ['visits', 'today'],
    queryFn: () => api.get('/visits/today').then(res => res.data.data),
  });

  const { data: unreadChatData } = useQuery({
    queryKey: ['chat-unread'],
    queryFn: () => api.get('/chat/unread-count').then(res => res.data.data),
    refetchInterval: 30000,
  });

  const unreadChatCount: number = unreadChatData?.unreadCount || 0;

  const { data: bonusPreview } = useQuery({
    queryKey: ['bonus-preview'],
    queryFn: () => api.get(`/bonus-rules/preview/${user?.id}`).then(res => res.data.data),
    enabled: !!user?.id,
  });

  const { data: journeyData } = useQuery({
    queryKey: ['journey', 'today'],
    queryFn: () => api.get('/journeys/today').then(res => res.data.data).catch(() => null),
    refetchInterval: 60000,
  });

  const visitedCount = todayVisits?.filter((v: any) => v.status === 'COMPLETED').length || 0;
  const totalStores = todayRoute?.stores?.length || 0;

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="bg-primary text-white p-4 pb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm opacity-80">Olá,</p>
            <h1 className="text-xl font-bold">{user?.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLocation('/conversations')}
              className="p-2 rounded-full bg-white/20 relative"
            >
              <MessageCircle className="h-6 w-6" />
              {unreadChatCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadChatCount > 9 ? '9+' : unreadChatCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setLocation('/profile')}
              className="p-2 rounded-full bg-white/20"
            >
              <User className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl p-4 text-gray-900 shadow-lg -mb-12">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">Progresso de Hoje</span>
            <span className="text-lg font-bold text-primary">
              {visitedCount}/{totalStores}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: totalStores > 0 ? `${(visitedCount / totalStores) * 100}%` : '0%' }}
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 pt-16 space-y-4">
        {/* Journey Card */}
        <JourneyCard />

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setLocation('/route')}
            className="flex flex-col items-center gap-2 p-6 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
          >
            <div className="p-3 rounded-full bg-blue-100">
              <Route className="h-6 w-6 text-blue-600" />
            </div>
            <span className="font-medium text-gray-900">Meu Roteiro</span>
            <span className="text-xs text-gray-500">{totalStores} lojas hoje</span>
          </button>

          <button
            onClick={() => setLocation('/sync')}
            className="flex flex-col items-center gap-2 p-6 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
          >
            <div className="p-3 rounded-full bg-green-100">
              <RefreshCw className="h-6 w-6 text-green-600" />
            </div>
            <span className="font-medium text-gray-900">Sincronizar</span>
            <span className="text-xs text-gray-500">Atualizar dados</span>
          </button>

          <button
            onClick={() => setLocation('/training')}
            className="flex flex-col items-center gap-2 p-6 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
          >
            <div className="p-3 rounded-full bg-indigo-100">
              <GraduationCap className="h-6 w-6 text-indigo-600" />
            </div>
            <span className="font-medium text-gray-900">Treinar</span>
            <span className="text-xs text-gray-500">Certificações</span>
          </button>

          <button
            onClick={() => setLocation('/pesquisa-pdv')}
            className="flex flex-col items-center gap-2 p-6 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50"
          >
            <div className="p-3 rounded-full bg-sky-100">
              <ClipboardList className="h-6 w-6 text-sky-600" />
            </div>
            <span className="font-medium text-gray-900">Pesquisa PDV</span>
            <span className="text-xs text-gray-500">Responder por loja</span>
          </button>
        </div>

        {/* Expense / Fuel Buttons */}
        <ExpenseButton
          allowExpenses={!!journeyData}
          allowFuel={!!journeyData?.has_vehicle}
        />

        {/* Bonus Goals Widget */}
        {bonusPreview && bonusPreview.results.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Minhas Metas</h2>
              <span className="text-sm font-bold text-green-600">
                R$ {bonusPreview.total_achieved.toFixed(2)}
              </span>
            </div>
            <div className="divide-y divide-gray-100">
              {bonusPreview.results.map((bonus: any) => (
                <div key={bonus.rule_id} className="flex items-center gap-3 p-4">
                  <div className={`p-1.5 rounded-full ${bonus.achieved ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {bonus.achieved ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Target className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{bonus.rule_name}</p>
                    <p className="text-xs text-gray-500">
                      Atual: {bonus.metric_value.toFixed(1)} | Meta: {bonus.operator === 'GTE' ? '>=' : '<='} {bonus.threshold}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${bonus.achieved ? 'text-green-600' : 'text-gray-400'}`}>
                    R$ {bonus.bonus_value.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Today's Stores */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Próximas Visitas</h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              Carregando...
            </div>
          ) : !todayRoute?.stores?.length ? (
            <div className="p-8 text-center text-gray-500">
              Nenhuma visita programada para hoje
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {todayRoute.stores.slice(0, 5).map((store: any) => (
                <button
                  key={store.store_id}
                  onClick={() => setLocation(`/visit/${store.store_id}`)}
                  className="w-full flex items-center gap-4 p-4 text-left active:bg-gray-50"
                >
                  <div className={`p-2 rounded-full ${store.visited ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {store.visited ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <MapPin className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{store.store_name}</p>
                    <p className="text-sm text-gray-500 truncate">{store.store_address}</p>
                  </div>
                  <span className="text-xs text-gray-400">#{store.order}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-2">
        <div className="flex items-center justify-around">
          <button className="flex flex-col items-center gap-1 p-2 text-primary">
            <MapPin className="h-6 w-6" />
            <span className="text-xs font-medium">Início</span>
          </button>
          <button
            onClick={() => setLocation('/route')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <Route className="h-6 w-6" />
            <span className="text-xs">Roteiro</span>
          </button>
          <button
            onClick={() => setLocation('/sync')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <RefreshCw className="h-6 w-6" />
            <span className="text-xs">Sync</span>
          </button>
          <button
            onClick={() => setLocation('/achievements')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <Trophy className="h-6 w-6" />
            <span className="text-xs">Ranking</span>
          </button>
          <button
            onClick={() => setLocation('/profile')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <User className="h-6 w-6" />
            <span className="text-xs">Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
